/**
 * Cliente API para SmartTutor (LMS Microservice)
 *
 * Mismo patron que apiClient.js pero conecta a SmartTutor (puerto 8020).
 * Usa el mismo token JWT de SmartHub para autenticacion.
 */

import axios from 'axios';
import i18next from 'i18next';
import { getSmartTutorApiUrl } from '@/config/api';

const LMS_TIMEOUT = 10000;

const buildUrl = (endpoint, pathParams = {}) => {
  const baseUrl = getSmartTutorApiUrl();
  let url = `${baseUrl}${endpoint}`;

  Object.keys(pathParams).forEach(key => {
    url = url.replace(`:${key}`, encodeURIComponent(pathParams[key]));
  });

  return url;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const lmsApiClient = {
  get: async (endpoint, pathParams = {}, queryParams = {}) => {
    const url = buildUrl(endpoint, pathParams);
    const response = await axios.get(url, {
      headers: getAuthHeaders(),
      params: queryParams,
      timeout: LMS_TIMEOUT,
    });
    return response.data;
  },

  post: async (endpoint, pathParams = {}, data = {}, options = {}) => {
    const url = buildUrl(endpoint, pathParams);
    const response = await axios.post(url, data, {
      headers: getAuthHeaders(),
      timeout: options.timeout || LMS_TIMEOUT,
    });
    return response.data;
  },

  put: async (endpoint, pathParams = {}, data = {}) => {
    const url = buildUrl(endpoint, pathParams);
    const response = await axios.put(url, data, {
      headers: getAuthHeaders(),
      timeout: LMS_TIMEOUT,
    });
    return response.data;
  },

  patch: async (endpoint, pathParams = {}, data = {}) => {
    const url = buildUrl(endpoint, pathParams);
    const response = await axios.patch(url, data, {
      headers: getAuthHeaders(),
      timeout: LMS_TIMEOUT,
    });
    return response.data;
  },

  delete: async (endpoint, pathParams = {}) => {
    const url = buildUrl(endpoint, pathParams);
    const response = await axios.delete(url, {
      headers: getAuthHeaders(),
      timeout: LMS_TIMEOUT,
    });
    return response.data;
  },

  /**
   * POST that returns an SSE stream (EventSource via fetch).
   * @param {string} endpoint
   * @param {object} pathParams
   * @param {object} data - JSON body
   * @param {object} callbacks - { onMeta, onToken, onDone, onError }
   */
  streamPost: async (endpoint, pathParams = {}, data = {}, callbacks = {}) => {
    const url = buildUrl(endpoint, pathParams);
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      let detail = i18next.t('lms:common.errors.streamingError');
      try { detail = JSON.parse(errorBody).detail || detail; } catch { /* ignore */ }
      throw new Error(detail);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let currentEvent = null;
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ') && currentEvent) {
          const rawData = line.slice(6);
          try {
            const parsed = JSON.parse(rawData);
            if (currentEvent === 'status' && callbacks.onStatus) callbacks.onStatus(parsed);
            else if (currentEvent === 'meta' && callbacks.onMeta) callbacks.onMeta(parsed);
            else if (currentEvent === 'token' && callbacks.onToken) callbacks.onToken(parsed);
            else if (currentEvent === 'done' && callbacks.onDone) callbacks.onDone(parsed);
            else if (currentEvent === 'error' && callbacks.onError) callbacks.onError(parsed);
          } catch { /* skip malformed */ }
          currentEvent = null;
        }
      }
    }
  },

  upload: async (endpoint, pathParams = {}, file, fieldName = 'file') => {
    const url = buildUrl(endpoint, pathParams);
    const formData = new FormData();
    formData.append(fieldName, file);
    const response = await axios.post(url, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export default lmsApiClient;
